import React, { useState } from 'react';
import { adminAPI } from '../../services/api';
import { 
    Upload, FileText, CheckCircle, AlertCircle, 
    Loader, FileArchive, Send, Mail, UserPlus 
} from 'lucide-react';

const ResumeManagement = ({ classes }) => {
    const [bulkFile, setBulkFile] = useState(null);
    const [singleFile, setSingleFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    const handleBulkUpload = async (e) => {
        e.preventDefault();
        if (!bulkFile) return;

        setLoading(true);
        setError('');
        setResult(null);

        const formData = new FormData();
        formData.append('resume_zip', bulkFile);

        try {
            const data = await adminAPI.bulkUploadResumes(formData);
            setResult({ type: 'bulk', ...data });
            setBulkFile(null);
        } catch (err) {
            setError(err.response?.data?.error || 'Bulk upload failed');
        } finally {
            setLoading(false);
        }
    };

    const handleSingleUpload = async (e) => {
        e.preventDefault();
        if (!singleFile) return;

        setLoading(true);
        setError('');
        setResult(null);

        const formData = new FormData();
        formData.append('resume', singleFile);

        try {
            const data = await adminAPI.uploadSingleResume(formData);
            setResult({ type: 'single', ...data });
            setSingleFile(null);
        } catch (err) {
            setError(err.response?.data?.error || 'Single upload failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-8 max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                
                {/* Single Resume Upload */}
                <div className={`${classes.card} p-8 rounded-[2rem] border ${classes.border} flex flex-col`}>
                    <div className="flex items-center gap-4 mb-6">
                        <div className="p-3 bg-blue-500/10 rounded-xl text-blue-500">
                            <FileText size={24} />
                        </div>
                        <div>
                            <h3 className="text-xl font-black text-white uppercase">LinkedIn Single Import</h3>
                            <p className="text-xs text-zinc-500 font-bold uppercase tracking-wider">Upload PDF Resume</p>
                        </div>
                    </div>

                    <form onSubmit={handleSingleUpload} className="space-y-4 mt-auto">
                        <div className={`border-2 border-dashed ${classes.border} rounded-2xl p-8 text-center hover:border-purple-500/50 transition-colors cursor-pointer relative`}>
                            <input 
                                type="file" 
                                accept=".pdf" 
                                onChange={(e) => setSingleFile(e.target.files[0])}
                                className="absolute inset-0 opacity-0 cursor-pointer"
                            />
                            <Upload className="mx-auto text-zinc-500 mb-2" size={32} />
                            <p className="text-sm font-bold text-zinc-400">
                                {singleFile ? singleFile.name : "Select PDF Resume"}
                            </p>
                        </div>
                        <button 
                            type="submit" 
                            disabled={loading || !singleFile}
                            className={`w-full py-4 rounded-xl ${classes.button} font-black uppercase tracking-widest disabled:opacity-50`}
                        >
                            {loading ? <Loader className="animate-spin mx-auto" /> : "Process Resume"}
                        </button>
                    </form>
                </div>

                {/* Bulk Resume Upload */}
                <div className={`${classes.card} p-8 rounded-[2rem] border ${classes.border} flex flex-col`}>
                    <div className="flex items-center gap-4 mb-6">
                        <div className="p-3 bg-purple-500/10 rounded-xl text-purple-500">
                            <FileArchive size={24} />
                        </div>
                        <div>
                            <h3 className="text-xl font-black text-white uppercase">Bulk ZIP Import</h3>
                            <p className="text-xs text-zinc-500 font-bold uppercase tracking-wider">Upload ZIP of PDF Resumes</p>
                        </div>
                    </div>

                    <form onSubmit={handleBulkUpload} className="space-y-4 mt-auto">
                        <div className={`border-2 border-dashed ${classes.border} rounded-2xl p-8 text-center hover:border-purple-500/50 transition-colors cursor-pointer relative`}>
                            <input 
                                type="file" 
                                accept=".zip" 
                                onChange={(e) => setBulkFile(e.target.files[0])}
                                className="absolute inset-0 opacity-0 cursor-pointer"
                            />
                            <Upload className="mx-auto text-zinc-500 mb-2" size={32} />
                            <p className="text-sm font-bold text-zinc-400">
                                {bulkFile ? bulkFile.name : "Select ZIP File"}
                            </p>
                        </div>
                        <button 
                            type="submit" 
                            disabled={loading || !bulkFile}
                            className={`w-full py-4 rounded-xl bg-zinc-800 text-white font-black uppercase tracking-widest hover:bg-zinc-700 disabled:opacity-50`}
                        >
                            {loading ? <Loader className="animate-spin mx-auto" /> : "Extract & Import"}
                        </button>
                    </form>
                </div>
            </div>

            {/* Results Display */}
            {error && (
                <div className="p-4 bg-red-500/10 border border-red-500/50 rounded-2xl text-red-500 flex items-center gap-3 font-bold">
                    <AlertCircle size={20} /> {error}
                </div>
            )}

            {result && (
                <div className={`${classes.card} p-8 rounded-[2rem] border ${classes.border} animate-in fade-in slide-in-from-bottom-4`}>
                    <div className="flex items-center gap-3 mb-6">
                        <CheckCircle className="text-green-500" size={24} />
                        <h3 className="text-xl font-black text-white uppercase">Import Summary</h3>
                    </div>

                    {result.type === 'single' ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="p-4 bg-white/5 rounded-xl">
                                <p className="text-[10px] font-black uppercase text-zinc-500 mb-1">Candidate</p>
                                <p className="text-white font-bold">{result.candidate.first_name} {result.candidate.last_name}</p>
                            </div>
                            <div className="p-4 bg-white/5 rounded-xl">
                                <p className="text-[10px] font-black uppercase text-zinc-500 mb-1">Email</p>
                                <p className="text-white font-bold">{result.candidate.email}</p>
                            </div>
                            <div className="p-4 bg-white/5 rounded-xl">
                                <p className="text-[10px] font-black uppercase text-zinc-500 mb-1">Account Created</p>
                                <p className={result.is_new_user ? "text-green-500 font-black" : "text-zinc-400 font-bold"}>
                                    {result.is_new_user ? "YES (Welcome Email Sent)" : "Existing User"}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="p-6 bg-green-500/10 rounded-2xl border border-green-500/20">
                                    <p className="text-4xl font-black text-green-500">{result.processed}</p>
                                    <p className="text-[10px] font-black uppercase text-green-500/60">Success</p>
                                </div>
                                <div className="p-6 bg-red-500/10 rounded-2xl border border-red-500/20">
                                    <p className="text-4xl font-black text-red-500">{result.failed}</p>
                                    <p className="text-[10px] font-black uppercase text-red-500/60">Failed</p>
                                </div>
                            </div>
                            {result.errors && result.errors.length > 0 && (
                                <div className="mt-4 p-4 bg-white/5 rounded-xl max-h-40 overflow-y-auto">
                                    <p className="text-xs font-black uppercase text-red-400 mb-2">Error Details:</p>
                                    {result.errors.map((err, i) => (
                                        <p key={i} className="text-xs text-zinc-500 mb-1">• {err}</p>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ResumeManagement;